// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IPresentation } from '@/types';

interface GetPresentationArgs {
  params?: Record<string, string | number>;
}

const addPresentation = createAsyncThunk('adminPresentation/addPresentation', async (presentationData: IPresentation, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/admin/presentations', presentationData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding presentation failed');
  }
});

const getAllPresentations = createAsyncThunk('adminPresentation/getPresentations', async ({ params = {} }: GetPresentationArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/admin/presentations${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving presentations failed');
  }
});

const getPresentation = createAsyncThunk('adminPresentation/getEvent', async (slugOrId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/admin/presentations/${slugOrId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single presentation failed');
  }
});

const updatePresentation = createAsyncThunk('adminPresentation/updatePresentation', async (params: { slugOrId: string; updatedData: Partial<IPresentation> }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/admin/presentations/${params.slugOrId}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating presentation failed');
  }
});

const deletePresentation = createAsyncThunk('adminPresentation/deletePresentation', async (slugOrId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/admin/presentations/${slugOrId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting presentation failed');
  }
});

const adminPresentationSlice = createSlice({
  name: 'adminPresentation',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addPresentation
      .addCase(addPresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addPresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getAllPresentations
      .addCase(getAllPresentations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPresentations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getAllPresentations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getPresentation
      .addCase(getPresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getPresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updatePresentation
      .addCase(updatePresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updatePresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deletePresentation
      .addCase(deletePresentation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePresentation.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deletePresentation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = adminPresentationSlice.actions;
export { addPresentation, deletePresentation, getAllPresentations, getPresentation, updatePresentation };
export default adminPresentationSlice.reducer;
